/* eslint-disable max-lines, react/no-multi-comp */
import { Input, Label } from "@altimateai/lego";
import { Card, CardContent, Stack } from "@altimateai/lego";
import { Button } from "@altimateai/lego";
import styles from "./styles.module.scss";
import { useMutation, useQuery } from "@tanstack/react-query";
import classNames from "classnames";
import { useEffect, useRef, useState } from "react";
import { ArrayInput, ArrayInputHandle } from "../../components/form/Input";
import { MarkdownEditorHandle } from "../../components/markdownEditor";
import { MarkdownEditor } from "../../components/markdownEditor";
import {
  getTableInsightsById,
  getWarehouseMetadata,
  createExperiment,
  getSchemaMetadata,
  getWarehouseInsights,
  getQueryGroupDetail,
} from "../../helpers/apis";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppState } from "@/modules/app/useAppContext";
import dayjs from "dayjs";
import { EntityTypeLabels, GET_SCHEMA_METADATA, GET_WAREHOUSE_METADATA } from "./constants";
import { Dropdown } from "../../components/form";
import { RelativeComponentLoader } from "../../components/loader/Loader";
import { useBreadcrumb } from "@/components/breadcrumbs";
import { ExperimentsTracking } from "@/lib/tracking/experiments";
import { SelectOption } from "../../components/form/Select";
import { WarehouseInsightsItem } from "../warehouse/types";
import { QueryGroup } from "../query/types";

interface EntityState {
  entityType: string;
  warehouse?: string[];
  schema?: string[];
  datasets?: string | string[];
  queryHash?: string;
  jobId?: string;
  name?: string;
}

interface WarehouseInsightOption extends SelectOption {
  _value: WarehouseInsightsItem;
}

interface LocationState {
  warehouse?: unknown;
  datasets?: unknown;
  tableId?: unknown;
  queryHash?: unknown;
  jobId?: unknown;
  name?: unknown;
}

function isLocationState(value: unknown): value is LocationState {
  return typeof value === "object" && value !== null;
}

function isQueryGroup(value: unknown): value is QueryGroup {
  return (
    typeof value === "object" &&
    value !== null &&
    "tags" in value &&
    Array.isArray((value as QueryGroup).tags)
  );
}

const ExperimentationAdd = () => {
  useBreadcrumb("New", {
    defaultParents: [{ label: "Experimentation", pathname: "/experimentation" }],
  });
  const [name, setName] = useState("");
  const [experimentStart, setExperimentStart] = useState(dayjs().format("YYYY-MM-DD"));
  const [experimentEnd, setExperimentEnd] = useState(dayjs().format("YYYY-MM-DD"));
  const warehouseRef = useRef<ArrayInputHandle>(null);
  const schemaRef = useRef<ArrayInputHandle>(null);
  const warehouseInsightRef = useRef<ArrayInputHandle>(null);
  const datasetsInsightRef = useRef<ArrayInputHandle>(null);
  const groupsInsightRef = useRef<ArrayInputHandle>(null);
  const metricsRef = useRef<ArrayInputHandle>(null);
  const hypothesisRef = useRef<MarkdownEditorHandle>(null);
  const instructionRef = useRef<MarkdownEditorHandle>(null);
  const { loggedinUser: user } = useAppState();
  const [warehouseInsightsOptions, setWarehouseInsightsOptions] = useState<
    WarehouseInsightOption[]
  >([]);
  const [datasetsInsightsOptions, setDatasetsInsightsOptions] = useState<SelectOption[]>([]);
  const [groupsInsightsOptions, setGroupsInsightsOptions] = useState<SelectOption[]>([]);
  const [entities, setEntities] = useState<EntityState>({ entityType: "" });
  const [error, setError] = useState("");
  const [effortLevel, setEffortlevel] = useState("");
  const { state } = useLocation();
  useEffect(() => {
    console.log("locationState:", state);
    if (!isLocationState(state)) return;
    if (state.warehouse) {
      setEntities({
        entityType: "warehouse",
        warehouse: [String(state.warehouse)],
      });
    } else if (state.datasets) {
      setEntities({
        entityType: "datasets",
        datasets: String(state.datasets),
        name: typeof state.name === "string" ? state.name : undefined,
      });
      getTableInsightsById(String(state.tableId)).then(data => {
        if (!data) return;
        setDatasetsInsightsOptions(
          (data.tags || []).map(t => ({ label: t.label, value: t.label }))
        );
      });
    } else if (state.queryHash) {
      setEntities({
        entityType: "query_groups",
        queryHash: String(state.queryHash),
        name: typeof state.name === "string" ? state.name : undefined,
      });
      getQueryGroupDetail(String(state.queryHash)).then(data => {
        if (!isQueryGroup(data)) return;
        setGroupsInsightsOptions(data.tags.map(t => ({ label: t, value: t })));
      });
    } else if (state.jobId) {
      setEntities({
        entityType: "query_jobs",
        jobId: String(state.jobId),
        name: typeof state.name === "string" ? state.name : undefined,
      });
    }
  }, []);
  const navigate = useNavigate();

  const { data: warehouseMetadata, isLoading: isWarehouseLoading } = useQuery({
    queryKey: [GET_WAREHOUSE_METADATA],
    queryFn: () => getWarehouseMetadata(),
  });
  const { data: schemaMetadata, isLoading: isSchemaLoading } = useQuery({
    queryKey: [GET_SCHEMA_METADATA],
    queryFn: () => getSchemaMetadata(),
  });

  const { mutate } = useMutation({
    mutationFn: (): Promise<{ ok: boolean } | undefined> => {
      setError("");
      const entity_type = entities?.entityType;
      if (!entity_type) {
        setError("Please select entity type");
        return Promise.resolve(undefined);
      }
      let _entities: Array<{
        entity_rk: string;
        entity_type: string;
        entity_name: string;
        insights?: Array<{ tag_rk: string }>;
      }> = [];
      if (entity_type === "warehouse") {
        const warehouseValues = warehouseRef.current?.getValue() ?? [];
        _entities = warehouseValues.map(e => ({
          entity_rk: e.value,
          entity_type,
          entity_name: e.label,
        }));
        if (_entities.length > 0) {
          const selectedInsightValues = new Set(
            (warehouseInsightRef.current?.getValue() ?? []).map(i => i.value)
          );
          const selectedInsights = warehouseInsightsOptions.filter(o =>
            selectedInsightValues.has(o.value)
          );
          _entities.forEach(e => {
            e["insights"] = selectedInsights
              .filter(i => i._value.warehouse_rk === e.entity_rk)
              .map(i => ({ tag_rk: i._value.tag_rk }));
          });
        }
      } else if (entity_type === "datasets") {
        const _insights = datasetsInsightRef.current?.getValue() ?? [];
        const datasetsRk = Array.isArray(entities?.datasets)
          ? entities.datasets.join(",")
          : (entities?.datasets ?? "");
        _entities = [
          {
            entity_rk: datasetsRk,
            entity_type,
            entity_name: entities?.name ?? "",
            insights: _insights.map(i => ({ tag_rk: i.value })),
          },
        ];
      } else if (entity_type === "query_groups") {
        const _insights = groupsInsightRef.current?.getValue() ?? [];
        _entities = [
          {
            entity_rk: entities?.queryHash ?? "",
            entity_type,
            entity_name: entities?.name ?? "",
            insights: _insights.map(i => ({ tag_rk: i.value })),
          },
        ];
      } else if (entity_type === "query_jobs") {
        _entities = [
          {
            entity_rk: entities?.jobId ?? "",
            entity_type,
            entity_name: entities?.name ?? "",
          },
        ];
      } else if (entity_type === "schema") {
        const schemaValues = schemaRef.current?.getValue() ?? [];
        _entities = schemaValues.map(e => ({
          entity_rk: e.value,
          entity_type,
          entity_name: e.label,
        }));
      }

      const hypothesis = hypothesisRef.current?.getValue() ?? "";
      const instructions = instructionRef.current?.getValue() ?? "";
      const _experimentStart = dayjs(experimentStart);
      const _experimentEnd = dayjs(experimentEnd);
      if (!hypothesis) {
        setError("Hypothesis is required");
        return Promise.resolve(undefined);
      }

      if (!instructions) {
        setError("Instructions is required");
        return Promise.resolve(undefined);
      }

      if (_experimentEnd.diff(_experimentStart) < 0) {
        setError("End date should be later than start date");
        return Promise.resolve(undefined);
      }

      if (!effortLevel) {
        setError("Please select effort level for the experiment");
        return Promise.resolve(undefined);
      }

      const metrics = (metricsRef.current?.getValue() ?? []).map(item => ({
        metric_type: item.value,
      }));
      return createExperiment({
        name,
        hypothesis,
        experiment_start: _experimentStart.toISOString(),
        experiment_end: _experimentEnd.toISOString(),
        instructions,
        entities: _entities,
        metrics,
        effort_level: effortLevel,
      });
    },
    onSuccess: data => {
      if (data?.ok) {
        const entityCount =
          entities?.entityType === "warehouse"
            ? (warehouseRef.current?.getValue() ?? []).length
            : entities?.entityType === "schema"
              ? (schemaRef.current?.getValue() ?? []).length
              : 1;
        const metricCount = (metricsRef.current?.getValue() ?? []).length;
        ExperimentsTracking.created(name, effortLevel, entityCount, metricCount);
        navigate("/experimentation");
      }
    },
  });

  if (isWarehouseLoading || isSchemaLoading) {
    return <RelativeComponentLoader />;
  }
  return (
    <Stack direction="column">
      <h3 className="text-black">Experimentation</h3>
      <Card className="w-60">
        <CardContent className="al-pt-4">
          <form
            onSubmit={e => {
              e.preventDefault();
              mutate();
            }}
            className={styles.form}
          >
            <div className="mb-3">
              <Label className="form-label" htmlFor="name">
                Name
              </Label>
              <Input
                className={classNames(styles.form_input)}
                id="name"
                name="name"
                placeholder="Name"
                value={name}
                onChange={e => setName(e.target.value)}
              />
            </div>
            <div className="mb-3">
              <Label className="form-label">Entity type</Label>
              <Dropdown
                id="entity-type-dropdown"
                value={entities.entityType}
                options={["warehouse", "schema"].map(v => ({
                  label: v,
                  value: v,
                }))}
                onChange={v =>
                  setEntities(
                    v === "warehouse"
                      ? { entityType: v, warehouse: [] }
                      : { entityType: v, schema: [] }
                  )
                }
              >
                <div className={classNames(styles.form_input)}>
                  {EntityTypeLabels[entities?.entityType] || "Select entity type"}
                </div>
              </Dropdown>
            </div>
            {entities?.entityType === "warehouse" && warehouseMetadata && (
              <>
                <div className="mb-3">
                  <Label className="form-label">Warehouses</Label>
                  <ArrayInput
                    ref={warehouseRef}
                    placeholder="Warehouse name"
                    defaultValue={entities?.warehouse || []}
                    options={warehouseMetadata.map(w => ({
                      label: w.warehouse_name,
                      value: w.rk,
                    }))}
                    onChange={rks => {
                      getWarehouseInsights({ rks }).then(_warehouseInsights => {
                        if (!_warehouseInsights) return;
                        const _warehouseNameMap: Record<string, string> = {};
                        for (const w of warehouseMetadata) {
                          _warehouseNameMap[w.rk] = w.warehouse_name;
                        }
                        setWarehouseInsightsOptions(
                          _warehouseInsights.map(w => ({
                            label: `${w.tag_rk} (${_warehouseNameMap[w.warehouse_rk]})`,
                            value: `${w.tag_rk}-${w.warehouse_rk}`,
                            _value: w,
                          }))
                        );
                      });
                    }}
                  />
                </div>
                {warehouseInsightsOptions.length > 0 && (
                  <div className="mb-3">
                    <Label className="form-label">Insights</Label>
                    <ArrayInput
                      ref={warehouseInsightRef}
                      placeholder="Insight name"
                      options={warehouseInsightsOptions}
                    />
                  </div>
                )}
              </>
            )}

            {entities?.entityType === "schema" && schemaMetadata && (
              <>
                <div className="mb-3">
                  <Label className="form-label">Schemas</Label>
                  <ArrayInput
                    ref={schemaRef}
                    placeholder="Schema name"
                    defaultValue={entities?.schema || []}
                    options={schemaMetadata.map(w => ({
                      label: w.name,
                      value: w.rk,
                    }))}
                  />
                </div>
              </>
            )}

            {entities?.entityType === "datasets" && (
              <>
                <div className="mb-3">
                  <Label className="form-label">Datasets</Label>
                  <Input
                    className={classNames(styles.form_input)}
                    id="datasets"
                    name="datasets"
                    value={entities?.name}
                    disabled
                  />
                </div>
                {datasetsInsightsOptions.length > 0 && (
                  <div className="mb-3">
                    <Label className="form-label">Insights</Label>
                    <ArrayInput
                      ref={datasetsInsightRef}
                      placeholder="Insight name"
                      options={datasetsInsightsOptions}
                    />
                  </div>
                )}
              </>
            )}

            {entities?.entityType === "query_groups" && (
              <>
                <div className="mb-3">
                  <Label className="form-label">Query Groups</Label>
                  <Input
                    className={classNames(styles.form_input)}
                    id="query_groups"
                    name="query_groups"
                    value={entities?.name}
                    disabled
                  />
                </div>
                {groupsInsightsOptions.length > 0 && (
                  <div className="mb-3">
                    <Label className="form-label">Insights</Label>
                    <ArrayInput
                      ref={groupsInsightRef}
                      placeholder="Insight name"
                      options={groupsInsightsOptions}
                    />
                  </div>
                )}
              </>
            )}

            {entities?.entityType === "query_jobs" && (
              <div className="mb-3">
                <Label className="form-label">Query Jobs</Label>
                <Input
                  className={classNames(styles.form_input)}
                  id="query_jobs"
                  name="query_jobs"
                  value={entities?.name}
                  disabled
                />
              </div>
            )}

            <Stack gap="sm" className="mb-3">
              <div>
                <Label className="form-label">Owner</Label>
                <Input
                  className={classNames(styles.form_input)}
                  id="owner"
                  name="owner"
                  value={user?.first_name ?? ""}
                  disabled
                />
              </div>
              <div className="flex-grow">
                <Label className="form-label">Effort Level</Label>
                <Dropdown
                  id="experiment-add-dropdown-effort-level"
                  value={effortLevel}
                  options={[
                    { label: "Low", value: "low" },
                    { label: "Medium", value: "medium" },
                    { label: "High", value: "high" },
                  ]}
                  onChange={setEffortlevel}
                >
                  <div className={classNames(styles.form_input)}>
                    {effortLevel || "Select effort level"}
                  </div>
                </Dropdown>
              </div>
            </Stack>
            <div className="mb-3">
              <Label className="form-label">Hypothesis/Reason</Label>
              <div className={styles.markdown_input}>
                <MarkdownEditor ref={hypothesisRef} />
              </div>
            </div>
            <div className="mb-3">
              <Label className="form-label">Metrics</Label>
              <ArrayInput
                ref={metricsRef}
                placeholder="Metrics name"
                options={[
                  { label: "Cost", value: "cost" },
                  {
                    label: "Total Execution Time",
                    value: "total_execution_time",
                  },
                ]}
              />
            </div>
            <div className="mb-3">
              <Label className="form-label">Period</Label>
              <Stack gap="lg" className="al-w-full">
                <Stack gap="xs" align="center">
                  <div>From:</div>
                  <Input
                    className={classNames(styles.form_input)}
                    id="experiment_start"
                    name="experiment_start"
                    type="date"
                    value={experimentStart}
                    onChange={e => setExperimentStart(e.target.value)}
                  />
                </Stack>
                <Stack gap="xs" align="center">
                  <div>To:</div>
                  <Input
                    className={classNames(styles.form_input)}
                    id="experiment_end"
                    name="experiment_end"
                    type="date"
                    value={experimentEnd}
                    onChange={e => setExperimentEnd(e.target.value)}
                  />
                </Stack>
              </Stack>
            </div>
            <div className="mb-3">
              <Label className="form-label">Instructions</Label>
              <div className={styles.markdown_input}>
                <MarkdownEditor ref={instructionRef} />
              </div>
            </div>
            <div className={styles.buttons}>
              {error && <div className={styles.error_msg}>{error}</div>}
              <div className="spacer" />
              <Button
                variant="outline"
                className={styles.cancel_button}
                onClick={() => navigate("/experimentation")}
              >
                <span className={styles.text}>Cancel</span>
              </Button>
              <Button type="submit" className={styles.save_button}>
                <span className={styles.text}>Save</span>
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </Stack>
  );
};

export { ExperimentationAdd };
